/**
 * تقارير ولوحات معلومات مبنية على نفس سياسات الوصول للمهام.
 * معادلة معدل الإنجاز: المكتملة ÷ (الكل − الملغاة). الملغاة لا تُحتسب في المقام.
 */

import type { DataGateway, Filter } from '@/data/types';
import type { Task, TaskAssignee, TaskPriority, TaskStatus } from '@/domain/types';
import { isTaskOverdue, priorityLabel, taskStatusLabel } from './task-service';

export interface ReportFilters {
  companyId: string;
  from: string;
  to: string;
  branchId?: string | null;
  brandId?: string | null;
  assigneeMembershipId?: string | null;
}

export interface ReportBucket {
  key: string;
  label: string;
  total: number;
  completed: number;
  cancelled: number;
}

export interface ReportSummary {
  total: number;
  completed: number;
  cancelled: number;
  active: number;
  overdue: number;
  completionRate: number;
  onTimeRate: number;
  averageCompletionHours: number | null;
  byStatus: ReportBucket[];
  byPriority: ReportBucket[];
  byBranch: ReportBucket[];
  byAssignee: ReportBucket[];
}

export async function fetchReportTasks(
  gateway: DataGateway,
  filters: ReportFilters,
): Promise<{ tasks: Task[]; assignees: TaskAssignee[] }> {
  const filter: Record<string, unknown> = {
    companyId: { op: 'eq', value: filters.companyId },
    localDate: { op: 'gte', value: filters.from },
  };
  if (filters.branchId) filter['branchId'] = { op: 'eq', value: filters.branchId };
  if (filters.brandId) filter['brandId'] = { op: 'eq', value: filters.brandId };

  const tasks: Task[] = [];
  const pageSize = 500;
  for (let page = 1; page <= 40; page += 1) {
    const result = await gateway.table('tasks').query({
      filter: filter as Filter<Task>,
      orderBy: [{ field: 'localDate', direction: 'asc' }],
      offset: (page - 1) * pageSize,
      limit: pageSize,
      withCount: true,
    });
    tasks.push(...result.rows.filter((task) => task.localDate <= filters.to));
    if (result.rows.length < pageSize) break;
    if (result.total !== null && tasks.length >= result.total) break;
  }

  const ids = tasks.map((task) => task.id);
  const assignees: TaskAssignee[] = [];
  const chunkSize = 200;
  for (let index = 0; index < ids.length; index += chunkSize) {
    const chunk = ids.slice(index, index + chunkSize);
    const rows = await gateway.table('task_assignees').list({
      filter: { taskId: { op: 'in', value: chunk }, unassignedAt: { op: 'isNull' } },
    });
    assignees.push(...rows);
  }

  if (filters.assigneeMembershipId) {
    const allowedTaskIds = new Set(
      assignees
        .filter((assignee) => assignee.membershipId === filters.assigneeMembershipId)
        .map((assignee) => assignee.taskId),
    );
    return {
      tasks: tasks.filter((task) => allowedTaskIds.has(task.id)),
      assignees,
    };
  }
  return { tasks, assignees };
}

function bucketFromTasks(
  tasks: Task[],
  keyOf: (task: Task) => string,
  labelOf: (key: string) => string,
): ReportBucket[] {
  const map = new Map<string, ReportBucket>();
  for (const task of tasks) {
    const key = keyOf(task);
    const bucket = map.get(key) ?? { key, label: labelOf(key), total: 0, completed: 0, cancelled: 0 };
    bucket.total += 1;
    if (task.status === 'completed') bucket.completed += 1;
    if (task.status === 'cancelled') bucket.cancelled += 1;
    map.set(key, bucket);
  }
  return [...map.values()].sort((a, b) => b.total - a.total);
}

export function summarizeReport(
  tasks: Task[],
  assignees: TaskAssignee[],
  memberNames: Map<string, string> = new Map(),
): ReportSummary {
  const total = tasks.length;
  const completed = tasks.filter((task) => task.status === 'completed').length;
  const cancelled = tasks.filter((task) => task.status === 'cancelled').length;
  const active = tasks.filter(
    (task) => task.status === 'scheduled' || task.status === 'pending' || task.status === 'in_progress',
  ).length;
  const overdue = tasks.filter((task) => isTaskOverdue(task)).length;
  const denominator = total - cancelled;
  const completionRate = denominator > 0 ? completed / denominator : 0;

  const completedWithDue = tasks.filter(
    (task) => task.status === 'completed' && task.completedAt && task.dueAt,
  );
  const onTime = completedWithDue.filter(
    (task) => new Date(task.completedAt!).getTime() <= new Date(task.dueAt!).getTime(),
  ).length;
  const onTimeRate = completedWithDue.length > 0 ? onTime / completedWithDue.length : 0;

  const completedWithTimes = tasks.filter((task) => task.status === 'completed' && task.completedAt);
  const averageCompletionHours =
    completedWithTimes.length > 0
      ? completedWithTimes.reduce((sum, task) => {
          const start = new Date(task.startAt).getTime();
          const end = new Date(task.completedAt!).getTime();
          return sum + Math.max(0, end - start) / 3_600_000;
        }, 0) / completedWithTimes.length
      : null;

  const assigneeCounts = new Map<string, number>();
  for (const assignee of assignees) {
    assigneeCounts.set(assignee.membershipId, (assigneeCounts.get(assignee.membershipId) ?? 0) + 1);
  }
  const assigneeBuckets: ReportBucket[] = [...assigneeCounts.entries()].map(([membershipId, count]) => ({
    key: membershipId,
    label: memberNames.get(membershipId) ?? 'عضوية',
    total: count,
    completed: 0,
    cancelled: 0,
  }));

  return {
    total,
    completed,
    cancelled,
    active,
    overdue,
    completionRate,
    onTimeRate,
    averageCompletionHours,
    byStatus: bucketFromTasks(
      tasks,
      (task) => task.status,
      (key) => taskStatusLabel(key as TaskStatus),
    ),
    byPriority: bucketFromTasks(
      tasks,
      (task) => task.priority,
      (key) => priorityLabel(key as TaskPriority),
    ),
    byBranch: bucketFromTasks(
      tasks,
      (task) => task.branchId ?? 'none',
      (key) => (key === 'none' ? 'بدون فرع' : `فرع ${key.slice(0, 8)}`),
    ),
    byAssignee: assigneeBuckets,
  };
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function tasksToCsv(tasks: Task[], memberNames: Map<string, string> = new Map()): string {
  const header = ['العنوان', 'الحالة', 'الاعتماد', 'الأولوية', 'النوع', 'الفرع', 'الاستحقاق', 'الإنجاز', 'المنشئ'];
  const lines = [header.join(',')];
  for (const task of tasks) {
    lines.push(
      [
        task.title,
        taskStatusLabel(task.status),
        task.approvalState,
        priorityLabel(task.priority),
        task.taskType,
        task.branchId ?? '',
        task.dueAt ?? '',
        task.completedAt ?? '',
        task.createdByMembershipId
          ? memberNames.get(task.createdByMembershipId) ?? task.createdByMembershipId
          : task.createdByUserId ? 'المنصة' : '—',
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return `\uFEFF${lines.join('\r\n')}`;
}

export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
